/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React, { JSX, useEffect, useState } from 'react';
import { SankeyNode, SankeyNodeMinimal } from 'd3-sankey';
import { SankeyDataLink, SankeyDataNode, SankeyNodeAction } from 'types/sankey';

import { PathLink } from './sankey-link';

export type RectNode = SankeyNode<SankeyDataNode, SankeyDataLink> & {
  views?: Array<{
    [view: string]: SankeyNodeAction[];
  }>;
};
interface SankeyRectProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  color?: string;
  title?: string;
  node: RectNode;
  links: PathLink[];
  handleNodeClick: (
    e: React.MouseEvent<SVGRectElement, MouseEvent>,
    node: RectNode
  ) => void;
}

const getCoordinates = (node: RectNode) => {
  const { x0, x1, y0, y1 } = node;
  const width = x0 !== undefined && x1 !== undefined ? x1 - x0 : undefined;
  const height = y0 !== undefined && y1 !== undefined ? y1 - y0 : undefined;

  return {
    x: x0,
    y: y0,
    width,
    height,
  };
};

const highlightLink = (
  node: RectNode,
  links: PathLink[],
  highlight: boolean
) => {
  if (!node || !links) return;

  const nodeIndex = node.index;
  const connectedLinks = links.filter((link) => {
    const sourceIndex = (
      link.source as SankeyDataNode &
        SankeyNodeMinimal<SankeyDataNode, SankeyDataLink>
    ).index;
    const targetIndex = (
      link.target as SankeyDataNode &
        SankeyNodeMinimal<SankeyDataNode, SankeyDataLink>
    ).index;

    return sourceIndex === nodeIndex || targetIndex === nodeIndex;
  });

  connectedLinks.forEach((link) => {
    const sourceIndex = (
      link.source as SankeyDataNode &
        SankeyNodeMinimal<SankeyDataNode, SankeyDataLink>
    ).index;
    const targetIndex = (
      link.target as SankeyDataNode &
        SankeyNodeMinimal<SankeyDataNode, SankeyDataLink>
    ).index;

    const linkId = `path-link-${sourceIndex}-${targetIndex}`;
    const linkElement = document.getElementById(linkId);

    if (linkElement) {
      linkElement.style.strokeOpacity = highlight
        ? `${link.opacity + 0.2}`
        : `${link.opacity}`;
    }
  });
};

export const SankeyRect = ({
  color,
  node,
  links,
  handleNodeClick,
  ...rectProps
}: SankeyRectProps): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(true);
    }, 50); // Small delay to trigger animation
  }, []);
  return (
    <rect
      {...rectProps}
      id={`node-${node.key}`}
      fill={color}
      onClick={(e: React.MouseEvent<SVGRectElement, MouseEvent>) => {
        if (node.isDisabled) {
          e.stopPropagation();
          return;
        }
        handleNodeClick(e, node);
      }}
      onMouseOver={(e) => {
        if (node.isDisabled) return;
        e.currentTarget.style.opacity = `${node.opacity + 0.2}`;
        highlightLink(node, links, true);
      }}
      onMouseOut={(e) => {
        if (node.isDisabled) return;
        e.currentTarget.style.opacity = `${node.opacity}`;
        highlightLink(node, links, false);
      }}
      style={{
        cursor: node.isDisabled ? 'not-allowed' : 'pointer',
        opacity: isVisible ? node.opacity : 0, // Start invisible
        transform: isVisible ? 'translateY(0px)' : 'translateY(20px)', // Start lower
        transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
      }}
    ></rect>
  );
};

interface SankeyRectsProps {
  nodes: RectNode[];
  links: SankeyDataLink[];
  titleFunc?(node: RectNode): string;
  colorFunc?(node: RectNode): string;
  handleNodeClick: (
    e: React.MouseEvent<SVGRectElement, MouseEvent>,
    node: RectNode
  ) => void;
}

export const SankeyRects = ({
  nodes,
  links,
  titleFunc,
  colorFunc,
  handleNodeClick,
}: SankeyRectsProps): JSX.Element => {
  return (
    <g stroke="#000" style={{ width: '100%' }}>
      {nodes.map((node) => {
        const coords = getCoordinates(node);
        return (
          <SankeyRect
            {...coords}
            key={node.key}
            color={colorFunc?.(node)}
            title={titleFunc?.(node)}
            node={node}
            links={links}
            handleNodeClick={handleNodeClick}
          />
        );
      })}
    </g>
  );
};
